import React, {Fragment} from 'react';

import moment, {Moment} from 'moment-tz';
import {Link} from 'react-router';

import {MultipleAssignedAvatar} from 'app/components/general/avatars/MultipleAssignedAvatar';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {DocumentSequencedStepsStatusIcon} from 'app/general/doc-status';
import {CheckboxButton} from 'app/general/widgets/CheckboxButton';
import {DocumentIconWithNotifications} from 'app/notifly/components/DocumentIconWithNotifications';
import {DashboardItemPresentMembers} from 'app/presence/components/DashboardItemPresentMembers';
import {useTaskUpdate} from 'app/tasks/task-update-hook';
import {isOverdue, simpleGetProgressLabel} from 'app/tasks/utils';
import {cannotHoverAndCanTouch} from 'app/utils/browser';
import {gt} from 'app/utils/i18n';
import {deepDiffer} from 'app/utils/react-utils';

import {CompactTaskItemActionsButton} from './CompactTaskItemActionsButton';
import {StyledTaskItemComponent} from './StyledComponents';

import type {NotificationType} from 'app/notifly/types';
import type {SlimTaskInstanceSerializedType} from 'sweet-types';

interface Props {
  task: SlimTaskInstanceSerializedType;
  userTimezone: string;
  showNowLine?: boolean;
  notifications?: Array<NotificationType>;
}

export const CompactTaskItem = React.memo(
  ({task, userTimezone, showNowLine, notifications}: Props): JSX.Element => {
    const {toggleTaskCompleted} = useTaskUpdate();

    const localise = (date?: string | Date) => {
      return moment(date).tz(userTimezone);
    };

    const localisedDue = localise(task.due);
    const taskIsOverdue = isOverdue(task);
    const assigned_object = task.template.assigned_object;
    const [title, subTitle] = React.useMemo(() => getTitles(task), [task]);

    const isPast = React.useMemo(() => {
      const now = localise();
      const due = localise(task.due);
      return now.isAfter(due);
    }, [task.due]);

    const areTooltipsEnabled = React.useMemo(() => {
      const usingIE = /Trident\/[67]/.test(window.navigator.userAgent);
      return (
        !usingIE &&
        !cannotHoverAndCanTouch &&
        task.description != null &&
        task.description !== ''
      );
    }, []);

    function addTooltip(content: JSX.Element, subTitle: undefined | string) {
      return areTooltipsEnabled ? (
        <LazyTooltip hide={subTitle == null} tooltip={subTitle}>
          {content}
        </LazyTooltip>
      ) : (
        content
      );
    }

    const assignees = React.useMemo(
      () => [
        ...task.template.users,
        ...task.template.teams,
        ...task.template.roles,
      ],
      [task.template.users, task.template.teams, task.template.roles]
    );

    return (
      <StyledTaskItemComponent
        showNowLine={showNowLine}
        isPast={isPast}
        PresenceNode={
          <DashboardItemPresentMembers
            contentType="taskinstance"
            objectId={task.id}
          />
        }
        TimeNode={
          <Link
            to={task.html_url}
            className={`ml-auto ml-sm-0 ${taskIsOverdue ? 'text-danger' : ''}`}
          >
            {formatTime(localisedDue)}
          </Link>
        }
        AssignedNode={
          <MultipleAssignedAvatar
            className="ml-auto ml-sm-0"
            usersAndTeamsAndRoles={assignees}
            tooltipTitle={gt('Assigned to')}
          />
        }
        StatsNode={
          <Fragment>
            <PercentageBadge task={task} />
            {task.comment_count != null && task.comment_count > 0 ? (
              <a className="badge badge-light ml-1" href={task.comments_url}>
                <i className="far fa-comments" /> {task.comment_count}
              </a>
            ) : null}
          </Fragment>
        }
        ItemNameNode={addTooltip(
          <div className="text-truncate">
            <Link
              to={task.html_url}
              className="text-dark font-weight-bold d-block d-sm-inline"
            >
              <DocumentIconWithNotifications
                contentType={assigned_object.content_type}
                notifications={notifications}
                className="text-secondary font-weight-light text-secondary"
              />{' '}
              {title}
            </Link>
            {subTitle !== null && (
              <span className="pl-0 pl-sm-2 text-muted font-weight-light font-italic">
                {subTitle}
              </span>
            )}
          </div>,
          subTitle
        )}
        ActionsNode={
          <Fragment>
            <CompactTaskItemActionsButton task={task} />
            <div style={{width: 20}}>
              {!task.is_completeable ? (
                <DocumentSequencedStepsStatusIcon
                  isSequenced={
                    task.template.assigned_object.steps_blocked_at != null
                  }
                  className="btn px-1"
                  contentType={task.template.assigned_object.content_type}
                />
              ) : (
                <CheckboxButton
                  fixedWidth={true}
                  disabled={false}
                  onClick={() => toggleTaskCompleted(task)}
                  isChecked={task.completed != null}
                  className="btn btn-link btn-lg py-0 px-1"
                />
              )}
            </div>
          </Fragment>
        }
      />
    );
  },
  (props, nextProps) => {
    const {task, userTimezone, showNowLine, notifications} = props;
    return (
      showNowLine === nextProps.showNowLine &&
      userTimezone === nextProps.userTimezone &&
      !deepDiffer(notifications, nextProps.notifications) &&
      !deepDiffer(task, nextProps.task)
    );
  }
);

const formatTime = (date: Moment) => date.format('h:mm A');

function PercentageBadge({
  task,
}: {
  task: SlimTaskInstanceSerializedType;
}): JSX.Element {
  const {style, text} = simpleGetProgressLabel(task);
  return <span className={`badge badge-${style} text-truncate`}>{text}</span>;
}

function getTitles(
  task: SlimTaskInstanceSerializedType
): [string, string | undefined] {
  let titles: Array<string> = [];

  if (task.description != null) {
    titles.push(task.description);
  }
  titles.push(task.name);
  return titles as [string, string | undefined];
}
